import React, { useState, useRef, useEffect } from 'react';
import Icon from 'components/AppIcon';

const QUICK_ACTIONS = [
  { label: 'Make it harder', icon: 'TrendingUp', prompt: 'Make the game harder with more obstacles and faster enemies' },
  { label: 'Add particles', icon: 'Sparkles', prompt: 'Add particle effects and visual polish' },
  { label: 'More cinematic', icon: 'Film', prompt: 'Make the video more cinematic with dramatic lighting' },
  { label: 'Add music', icon: 'Music', prompt: 'Add background music and sound effects' },
  { label: 'Improve UI', icon: 'Layout', prompt: 'Improve the UI with better colors and typography' },
  { label: 'Add story', icon: 'BookOpen', prompt: 'Add a compelling storyline and narrative elements' },
];

export default function AICreatorAssistant({ currentGame, onApplySuggestion, isOpen, onToggle }) {
  const [messages, setMessages] = useState([
    { role: 'assistant', text: 'Hi! I\'m your LoopNest AI Helper. Tell me how to improve your creation, or pick a quick action below.' }
  ]);
  const [input, setInput] = useState('');
  const [isThinking, setIsThinking] = useState(false);
  const messagesEndRef = useRef(null);

  useEffect(() => {
    messagesEndRef?.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const sendMessage = async (text) => {
    if (!text?.trim() || isThinking) return;
    const userMsg = text?.trim();
    setInput('');
    setMessages(prev => [...prev, { role: 'user', text: userMsg }]);
    setIsThinking(true);

    await new Promise(r => setTimeout(r, 1200 + Math.random() * 800));

    const suggestions = [
      `Great idea! I'll enhance your ${currentGame?.mode === 'text-to-game' ? 'game' : 'creation'} with: ${userMsg}. Regenerating with these improvements...`,
      `I can do that! Adding "${userMsg}" to your project. This will make it much more engaging.`,
      `Perfect suggestion! I'll modify the ${currentGame?.title || 'project'} to include: ${userMsg}`,
      `Applying your changes now. The updated version will feature: ${userMsg}`,
    ];
    const response = suggestions?.[Math.floor(Math.random() * suggestions?.length)];

    setMessages(prev => [...prev, { role: 'assistant', text: response }]);
    setIsThinking(false);

    if (onApplySuggestion) {
      setTimeout(() => onApplySuggestion(userMsg), 500);
    }
  };

  if (!isOpen) {
    return (
      <button
        onClick={onToggle}
        className="flex items-center gap-2 px-3 py-2 rounded-xl border text-xs font-semibold transition-all hover:opacity-80"
        style={{ borderColor: 'rgba(99,102,241,0.4)', background: 'rgba(99,102,241,0.08)', color: 'var(--color-primary)', fontFamily: 'var(--font-heading)' }}
      >
        <div className="w-5 h-5 rounded-full flex items-center justify-center" style={{ background: 'var(--color-primary)' }}>
          <Icon name="Bot" size={11} color="white" />
        </div>
        AI Helper
        <Icon name="ChevronDown" size={12} color="var(--color-primary)" />
      </button>
    );
  }

  return (
    <div className="rounded-2xl overflow-hidden animate-fade-in-up" style={{ background: 'var(--color-card)', border: '1px solid rgba(99,102,241,0.3)', boxShadow: 'var(--shadow-md)' }}>
      {/* Header */}
      <div className="flex items-center justify-between px-4 py-3 border-b" style={{ borderColor: 'var(--color-border)', background: 'rgba(99,102,241,0.06)' }}>
        <div className="flex items-center gap-2">
          <div className="w-7 h-7 rounded-full flex items-center justify-center" style={{ background: 'linear-gradient(135deg, var(--color-primary), var(--color-secondary))' }}>
            <Icon name="Bot" size={14} color="white" />
          </div>
          <div>
            <p className="text-xs font-bold" style={{ color: 'var(--color-foreground)', fontFamily: 'var(--font-heading)' }}>LoopNest AI Helper</p>
            <p className="text-xs" style={{ color: 'var(--color-success)', fontFamily: 'var(--font-caption)' }}>● Online</p>
          </div>
        </div>
        <button onClick={onToggle} className="w-6 h-6 rounded-lg flex items-center justify-center hover:bg-[var(--color-muted)] transition-all">
          <Icon name="ChevronUp" size={13} color="var(--color-muted-foreground)" />
        </button>
      </div>
      {/* Messages */}
      <div className="flex flex-col gap-2 p-3 max-h-40 overflow-y-auto" style={{ scrollbarWidth: 'none' }}>
        {messages?.map((msg, i) => (
          <div key={i} className={`flex ${msg?.role === 'user' ? 'justify-end' : 'justify-start'}`}>
            <div
              className="max-w-[85%] px-3 py-2 rounded-xl text-xs leading-relaxed"
              style={{
                background: msg?.role === 'user' ? 'var(--color-primary)' : 'var(--color-muted)',
                color: msg?.role === 'user' ? 'white' : 'var(--color-foreground)',
                fontFamily: 'var(--font-body)',
                borderRadius: msg?.role === 'user' ? '12px 12px 2px 12px' : '12px 12px 12px 2px',
              }}
            >
              {msg?.text}
            </div>
          </div>
        ))}
        {isThinking && (
          <div className="flex justify-start">
            <div className="px-3 py-2 rounded-xl flex items-center gap-1.5" style={{ background: 'var(--color-muted)' }}>
              {[0, 1, 2]?.map(i => (
                <div key={i} className="w-1.5 h-1.5 rounded-full" style={{ background: 'var(--color-primary)', animation: `bounce 1s ease-in-out ${i * 0.15}s infinite` }} />
              ))}
            </div>
          </div>
        )}
        <div ref={messagesEndRef} />
      </div>
      {/* Quick actions */}
      <div className="px-3 pb-2 flex gap-1.5 overflow-x-auto" style={{ scrollbarWidth: 'none' }}>
        {QUICK_ACTIONS?.map((action) => (
          <button
            key={action?.label}
            onClick={() => sendMessage(action?.prompt)}
            disabled={isThinking}
            className="flex-shrink-0 flex items-center gap-1 text-xs px-2.5 py-1.5 rounded-full border transition-all hover:opacity-80 disabled:opacity-40"
            style={{ borderColor: 'var(--color-border)', background: 'var(--color-muted)', color: 'var(--color-foreground)', fontFamily: 'var(--font-caption)' }}
          >
            <Icon name={action?.icon} size={10} color="var(--color-muted-foreground)" />
            {action?.label}
          </button>
        ))}
      </div>
      {/* Input */}
      <div className="flex gap-2 p-3 pt-1 border-t" style={{ borderColor: 'var(--color-border)' }}>
        <input
          value={input}
          onChange={(e) => setInput(e?.target?.value)}
          onKeyDown={(e) => e?.key === 'Enter' && !e?.shiftKey && sendMessage(input)}
          placeholder="Ask AI to improve your creation..."
          disabled={isThinking}
          className="flex-1 px-3 py-2 rounded-xl border text-xs focus:outline-none focus:ring-2 focus:ring-[var(--color-ring)] transition-all"
          style={{ borderColor: 'var(--color-border)', background: 'var(--color-muted)', color: 'var(--color-foreground)', fontFamily: 'var(--font-body)' }}
        />
        <button
          onClick={() => sendMessage(input)}
          disabled={!input?.trim() || isThinking}
          className="w-8 h-8 rounded-xl flex items-center justify-center transition-all hover:opacity-80 disabled:opacity-40"
          style={{ background: 'var(--color-primary)' }}
        >
          <Icon name="Send" size={13} color="white" />
        </button>
      </div>
    </div>
  );
}
